import characterSizeMap from "./characterSizeMap.json";
import { cloneDeep, filter, map, set } from "lodash-es";
import { JudgeType } from "@/types/conditionJudgeNodeType";

/**
 * @description 从nodeData中获取锚点数据
 * @param {Object} cfg 节点的配置对象
 */
function getAnchorPoints(cfg) {
  return cfg.anchors;
}

/**
 * @description 计算单个字符的大小
 * @param {String} c 需要计算的字符
 */
function calCof(c) {
  const size = characterSizeMap[c];
  if (size) {
    return size;
  }
  return 0.5;
}

/**
 * @description 计算一段文本的像素值
 * @param {String} text 需要计算的文本
 * @param {Number} fontSize  字号
 */
function calculateFontWidth(text, fontSize) {
  if (!text) return 0;
  // 字排版：fontGap-每个字之间的间距
  const fontGap = 1;
  let width = 0;
  for (let i = 0; i < text.length; i += 1) {
    if (text.charCodeAt(i) > 0 && text.charCodeAt(i) < 128) {
      // 字排版：0.5-字母以及常见符号占多宽，可以做更细致的控制
      const cof = calCof(text[i]);
      width += cof * fontSize + fontGap;
    } else {
      width += fontSize + fontGap;
    }
  }
  console.log(text, width);
  return width;
}

// 判断字符是否为回车
function isCR(text) {
  if (text.charCodeAt(0) === 10) {
    return true;
  }
  return false;
}

/**
 * @description 将原始文本按照单行最大长度进行分行
 * @param {String} text     原始文本
 * @param {Number} fontSize 字号
 * @param {Number} maxWidth 单行最大宽度
 */
function splitText(text = "", fontSize, maxWidth) {
  if (!text) return [];
  const lines = [];
  let [curWidth, startIndex, i] = [0, 0, 0];
  const contentWidth = maxWidth - fontSize * 0.2;

  while (i < text.length) {
    if (isCR(text[i])) {
      const tmp = text.slice(startIndex, i);
      startIndex = i + 1;
      curWidth = 0;
      lines.push(tmp);
    }
    curWidth += calculateFontWidth(text[i], fontSize);
    // 字排版：0.2-每一行最右边还剩多少时换行
    if (curWidth > contentWidth) {
      const line = text.slice(startIndex, i + 1);
      startIndex = i + 1;
      curWidth = 0;
      lines.push(line);
    }
    i += 1;
  }
  if (curWidth !== 0) {
    const line = text.slice(startIndex);
    lines.push(line);
  }
  return lines;
}

const linkSymbol = "-";

function getAPIID(paramStr) {
  if (!paramStr) {
    return "";
  }
  const paramParts = paramStr.split(linkSymbol);
  const apiID = paramParts[2] || "";
  return apiID;
}

function handleOperate(newModelOpt, oldValue, newValue, type) {
  const _newValue = type === "edit" ? newValue : "";
  const {
    nodeData: { nodeType, branches, request, response, inputVariables },
  } = newModelOpt;
  if (nodeType === "conditionalBranchNode") {
    const _branches = map(branches, (branch) => {
      return {
        ...branch,
        condition: map(branch.condition, (v) => {
          return {
            ...v,
            variableKey: v.variableKey === oldValue ? _newValue : v.variableKey,
          };
        }),
      };
    });
    set(newModelOpt, "nodeData.branches", _branches);
  }
  if (nodeType === "interfaceInvocationNode") {
    const reqBody = map(request?.body, (item) => {
      return {
        ...item,
        key: item.varType === 2 && item.key === oldValue ? _newValue : item.key,
      };
    });
    const resBody = map(response?.body, (item) => {
      return {
        ...item,
        key: item.key === oldValue ? _newValue : item.key,
      };
    });
    set(newModelOpt, "nodeData.request.body", reqBody);
    set(newModelOpt, "nodeData.response.body", resBody);
  }
  if (nodeType === "formSaveNode") {
    const _inputVariables = map(inputVariables, (item) => {
      return {
        ...item,
        variableKey:
          item.variableKey === oldValue ? _newValue : item.variableKey,
      };
    });
    set(newModelOpt, "nodeData.inputVariables", _inputVariables);
  }
  if (nodeType === "startNode") {
    const _inputVariables = map(inputVariables, (item) => {
      return {
        ...item,
        name: item.name === oldValue ? _newValue : item.name,
      };
    });
    set(newModelOpt, "nodeData.inputVariables", _inputVariables);
  }
}

function updateVarNode(xGraph, { index, type, oldValue, newValue }) {
  if (!["edit", "delete"].includes(type)) return;
  const graphNodes = xGraph.value.getNodes();
  filter(graphNodes, (node) => {
    const nodeModel = node.getModel();
    const nodeData = nodeModel.nodeData;
    const { nodeType } = nodeData;
    return [
      "formSaveNode",
      "interfaceInvocationNode",
      "conditionalBranchNode",
    ].includes(nodeType);
  }).map((node) => {
    const nodeModel = node.getModel();
    const newModelOpt = cloneDeep(nodeModel);
    const id = newModelOpt.id;
    const _node = xGraph.value.findById(id);
    if (!_node) return;
    handleOperate(newModelOpt, oldValue, newValue, type);

    node.update(newModelOpt);
  });
}

/**
 * 从节点列表中提取所有信息提取节点的信息
 * @param {Array} nodes - 节点列表
 * @returns {Array} 信息提取节点的信息
 */
function extractInfoNodesData(nodes) {
  return map(nodes, (node) => {
    const nodeModel = node.getModel ? node.getModel() : node;
    const nodeData = nodeModel.nodeData;
    return nodeData;
  })
    .filter((v) => v.nodeType === "infoExtractNode")
    .map((v) => {
      return {
        nodeName: v.nodeName,
        nodeId: v.id,
        infos: v.infos.map((info) => ({
          id: info.id,
          name: info.name,
          value: `\${${JudgeType.INFO}-${info.id}}`,
        })),
      };
    });
}

export {
  getAnchorPoints,
  calculateFontWidth,
  splitText,
  getAPIID,
  updateVarNode,
  extractInfoNodesData,
};
